import React from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Spinner } from '@material-tailwind/react';
import { useGetMaintenanceReports } from '../../custom-hooks/use-maintenance';
import { formatNumberWithCommasPakistani } from '../../utils/functions';
import { CATEGORY_META } from './utils';

// Validated via the dataviz skill's palette validator (node scripts/validate_palette.js
// "#2a78d6,#C2571A" --mode light --surface "#FBF8F3") — CVD ΔE 26.4, normal-vision ΔE
// 30.5, both well past the pass thresholds. Estimate/Actual are the only two series
// across every chart on this page (floors/categories/months are axis labels, not
// color-coded series), so this one validated pair covers the whole view. Actual reuses
// inv-brand itself, since it's the "real" committed figure; Estimate is a cooler,
// more provisional blue.
const ESTIMATE_COLOR = '#2a78d6';
const ACTUAL_COLOR = '#C2571A';

const tooltipStyle = { borderRadius: 12, border: '1px solid #E6DDCF', fontSize: 12, boxShadow: '0 12px 24px -12px rgba(40,22,8,0.25)' };
const pkrTooltip = (value) => `${formatNumberWithCommasPakistani(value)} PKR`;

const ChartCard = ({ title, subtitle, children, empty, emptyText = 'No data yet', height = 260 }) => (
    <div className="rounded-2xl border border-inv-cream-200 bg-white p-5">
        <div className="mb-4">
            <h3 className="font-bold text-inv-ink">{title}</h3>
            {subtitle && <p className="text-xs text-[#8C8175]">{subtitle}</p>}
        </div>
        {empty ? (
            <div className="flex items-center justify-center text-sm text-[#8C8175]" style={{ height }}>{emptyText}</div>
        ) : (
            <div className="w-full" style={{ height }}>{children}</div>
        )}
    </div>
);

const ReportsView = () => {
    const { data, isLoading } = useGetMaintenanceReports();
    const monthlyHistory = data?.monthlyHistory || [];
    const floorBreakdown = data?.floorBreakdown || [];
    const categoryBreakdown = (data?.categoryBreakdown || []).map((row) => ({
        ...row,
        label: CATEGORY_META[row.category]?.label || row.category,
    }));

    if (isLoading) {
        return <div className="flex min-h-[40vh] items-center justify-center"><Spinner className="h-8 w-8 text-inv-brand" /></div>;
    }

    return (
        <div className="flex flex-col gap-5">
            <ChartCard
                title="Monthly Cost History"
                subtitle="Estimate is counted in the month a work order was raised; Actual Cost in the month it was completed."
                empty={monthlyHistory.every((m) => !m.estimate && !m.actual)}
            >
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyHistory} margin={{ top: 8, right: 8, left: -20, bottom: 0 }} barGap={4} barCategoryGap="26%">
                        <CartesianGrid vertical={false} stroke="#e1e0d9" />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#898781' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <Tooltip contentStyle={tooltipStyle} cursor={{ fill: '#F1E9DC' }} formatter={pkrTooltip} />
                        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#52514e' }} />
                        <Bar dataKey="estimate" name="Estimate" fill={ESTIMATE_COLOR} radius={[4, 4, 0, 0]} maxBarSize={20} />
                        <Bar dataKey="actual" name="Actual Cost" fill={ACTUAL_COLOR} radius={[4, 4, 0, 0]} maxBarSize={20} />
                    </BarChart>
                </ResponsiveContainer>
            </ChartCard>

            <ChartCard
                title="Cost by Category — This Month"
                subtitle="Repair, Maintenance, Decor, Enhancement, Other"
                empty={categoryBreakdown.length === 0}
                height={Math.max(180, categoryBreakdown.length * 48)}
            >
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={categoryBreakdown}
                        layout="vertical"
                        margin={{ top: 8, right: 24, left: 8, bottom: 0 }}
                        barGap={4}
                        barCategoryGap="30%"
                    >
                        <CartesianGrid horizontal={false} stroke="#e1e0d9" />
                        <XAxis type="number" tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 11, fill: '#898781' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} />
                        <Tooltip contentStyle={tooltipStyle} cursor={{ fill: '#F1E9DC' }} formatter={pkrTooltip} />
                        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#52514e' }} />
                        <Bar dataKey="estimate" name="Estimate" fill={ESTIMATE_COLOR} radius={[0, 4, 4, 0]} maxBarSize={16} />
                        <Bar dataKey="actual" name="Actual Cost" fill={ACTUAL_COLOR} radius={[0, 4, 4, 0]} maxBarSize={16} />
                    </BarChart>
                </ResponsiveContainer>
            </ChartCard>

            <div className="rounded-2xl border border-inv-cream-200 bg-white p-5">
                <div className="mb-4">
                    <h3 className="font-bold text-inv-ink">Cost by Floor — This Month</h3>
                    <p className="text-xs text-[#8C8175]">A table, not a chart — the floor list can run long and its labels don't fit a chart axis cleanly.</p>
                </div>
                {floorBreakdown.length === 0 ? (
                    <p className="py-8 text-center text-sm text-[#8C8175]">No activity this month.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[560px] text-left text-sm">
                            <thead>
                                <tr className="border-b border-inv-cream-200 text-xs font-semibold uppercase tracking-wide text-[#8C8175]">
                                    <th className="px-3 py-2.5">Floor</th>
                                    <th className="px-3 py-2.5">Raised</th>
                                    <th className="px-3 py-2.5">Estimate</th>
                                    <th className="px-3 py-2.5">Completed</th>
                                    <th className="px-3 py-2.5">Actual Cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {floorBreakdown.map((row) => (
                                    <tr key={row.floorId} className="border-b border-inv-cream-100 last:border-0">
                                        <td className="px-3 py-2.5 font-medium text-inv-ink">{row.label}</td>
                                        <td className="px-3 py-2.5 text-[#6B675F]">{row.createdCount}</td>
                                        <td className="px-3 py-2.5 text-[#6B675F]">{formatNumberWithCommasPakistani(row.estimate)} PKR</td>
                                        <td className="px-3 py-2.5 text-[#6B675F]">{row.completedCount}</td>
                                        <td className="px-3 py-2.5 font-semibold text-inv-ink">{formatNumberWithCommasPakistani(row.actual)} PKR</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReportsView;
